<script lang="ts" setup>
import type { ResponseResult } from '~kui/types/formResult';

import { Heading } from '~ds/components/ui/typography';
import { defineField, defineFormConfig } from '~kui/components/KUI/Core/Form/util/form';
import { useI18n } from 'vue-i18n';
import { z } from 'zod';

interface Props {
  /**
   * Holds the step inert, natively — this submit creates the account, and a second one sent
   * underneath the first races it for the same session rather than doing nothing.
   */
  loading?: boolean;
  /**
   * Href for the terms page. The consumer resolves it, because only the app knows the
   * cluster path prefix — a bare `/terms` would always land on the root cluster.
   */
  termsPath?: string;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  termsPath: '/terms',
});

const emit = defineEmits<{
  submit: [];
}>();

const { t } = useI18n();

const formResult = ref<ResponseResult | null>(null);

const formConfig = defineFormConfig(
  {
    consent: defineField({
      type: 'checkbox',
      options: {
        label: t('general.auth.minAgeConsent.labelWithTermsLink', {
          termsLink: `<a href="${props.termsPath}" target="_blank" rel="noopener noreferrer" class="text-foreground font-medium underline">${t('general.auth.minAgeConsent.termsAndConditions')}</a>`,
        }),
        htmlLabel: true,
        required: true,
      },
      initialValue: false,
      schema: z.literal(true, t('general.auth.minAgeConsent.approvalRequired')),
    }),
  },
  t('general.auth.submitButton.submit'),
);

const handleSubmit = () => {
  formResult.value = null;
  emit('submit');
};
</script>

<template>
  <Heading
    size="xl"
    as="h1"
    class="text-center mb-3"
  >
    {{ t('general.auth.nearlyThere') }}
  </Heading>

  <div class="flex flex-col items-center">
    <KUICoreForm
      class="w-full"
      :form-config="formConfig"
      :form-result="formResult"
      :loading="loading"
      submit-button-size="lg"
      @submit="handleSubmit"
    />
  </div>
</template>
